import { useEffect, useMemo, useState } from "react";
import { DoraSelect } from "dora-shared/frontend";
import { useAppDispatch, useSelector } from "../../../../redux-store";
import {
  selectCurrentOrderTemplate,
  selectOrderTemplatesForClientTeamAndSearch,
} from "../../../../ducks/data/client-order-templates/selectors";
import {
  clearSelectedTemplate,
  loadAndUseClientCargoTemplate,
} from "../../../../ducks/data/client-order-templates";
import { useTranslation } from "react-i18next";

const ClientCargoTemplateSelect = ({
  clientId,
  teamId,
  onAfterSelectLoad,
}: {
  clientId: string;
  teamId: string;
  onAfterSelectLoad: () => void;
}) => {
  const templateOptions = useSelector(
    selectOrderTemplatesForClientTeamAndSearch({
      clientId,
      teamId,
      search: "",
    })
  );
  const dispatch = useAppDispatch();
  const selectedCurrentTemplate = useSelector(selectCurrentOrderTemplate);
  const { t } = useTranslation(["orderTemplates"]);

  const [value, setValue] = useState<string | undefined>(
    selectedCurrentTemplate?.id || undefined
  );

  useEffect(() => {
    if (selectedCurrentTemplate) {
      setValue(selectedCurrentTemplate?.id);
    }
  }, [selectedCurrentTemplate]);

  const options = useMemo(() => {
    const mappedOptions = templateOptions.map((t) => ({
      label: t.name,
      value: t.id,
    }));

    if (value) {
      const valueExists = mappedOptions.some((o) => o.value === value);
      if (!valueExists) {
        mappedOptions.unshift({
          label: selectedCurrentTemplate?.name || '(Template no longer available)',
          value: value,
        });
      }
    }

    return mappedOptions;
  }, [templateOptions, value, selectedCurrentTemplate]);

  const onOptionSelected = (templateId: string) => {
    if (templateId) {
      dispatch(loadAndUseClientCargoTemplate(templateId))
        .unwrap()
        .then(() => {
          onAfterSelectLoad();
        })
        .catch(() => {
          // The loadAndUseClientCargoTemplate thunk has already reported it and shown the error toast.
        });
    } else {
      setValue(undefined as any);
      dispatch(clearSelectedTemplate());
    }
  };

  return (
    <DoraSelect
      fullWidth
      options={options}
      freeSolo
      value={value ?? ''}
      placeholder={t("orderTemplates:createFromTemplate")}
      onChange={onOptionSelected}
      onInputChange={(_e, v) => {
        const matched = options.find((o) => o.label === v);
        if (!matched) {
          onOptionSelected(null as any);
        }
      }}
    />
  );
};

export default ClientCargoTemplateSelect;
